import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Menu, X, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

const NAV_KEYS = [
    { to: "/shop/women", key: "women" },
    { to: "/shop/men", key: "men" },
    { to: "/shop/fragrance", key: "fragrance" },
    { to: "/journal", key: "journal" },
    { to: "/lookbook", key: "lookbook" },
    { to: "/about", key: "studio" },
];

export const Header = () => {
    const { t } = useTranslation();
    const { count: cartCount, setOpen } = useCart();
    const { count: wishCount } = useWishlist();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [query, setQuery] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (q) {
            navigate(`/shop?q=${encodeURIComponent(q)}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQuery("");
        }
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 z-40 w-full transition-all duration-500 ${
                scrolled ? "backdrop-blur-2xl bg-[#0C0C0C]/75 border-b border-white/5" : "bg-transparent"
            }`}
        >
            <div className="border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl">
                <div className="container-rail flex h-7 items-center justify-end">
                    <LanguageSwitcher />
                </div>
            </div>
            <div className="container-rail flex h-16 items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                    <img
                        src="/savnt-logo.png"
                        alt="SAVNT"
                        className="h-9 w-9 object-contain transition-transform duration-500 group-hover:rotate-[-4deg]"
                    />
                    <div className="flex flex-col leading-none">
                        <span className="text-display text-2xl tracking-[0.15em]">SAVNT</span>
                        <span className="text-[8px] tracking-[0.32em] uppercase text-[#A3A3A3] mt-0.5 font-display">{t("nav.tagline")}</span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV_KEYS.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.key}`}
                            className={({ isActive }) =>
                                `text-overline transition-colors ${
                                    isActive ? "text-[#F4F4F0]" : "text-[#A3A3A3] hover:text-[#F4F4F0]"
                                }`
                            }
                        >
                            {t(`nav.${n.key}`)}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-1 sm:gap-2">
                    <button
                        onClick={() => setSearchOpen((v) => !v)}
                        data-testid="search-trigger"
                        className="p-2.5 text-[#F4F4F0] hover:bg-white/[0.04] transition-colors"
                        aria-label={t("nav.search")}
                    >
                        <Search className="h-[18px] w-[18px]" strokeWidth={1.25} />
                    </button>
                    <Link
                        to="/wishlist"
                        data-testid="wishlist-link"
                        className="relative p-2.5 text-[#F4F4F0] hover:bg-white/[0.04] transition-colors"
                        aria-label={t("nav.wishlist")}
                    >
                        <Heart className="h-[18px] w-[18px]" strokeWidth={1.25} />
                        {wishCount > 0 && (
                            <span
                                data-testid="wishlist-count"
                                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center bg-[#591C1C] px-1 text-[10px] text-white font-display"
                            >
                                {wishCount}
                            </span>
                        )}
                    </Link>
                    <button
                        data-testid="cart-trigger"
                        onClick={() => setOpen(true)}
                        className="relative p-2.5 text-[#F4F4F0] hover:bg-white/[0.04] transition-colors"
                        aria-label={t("nav.cart")}
                    >
                        <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.25} />
                        {cartCount > 0 && (
                            <span
                                data-testid="cart-count"
                                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center bg-[#EDEDE6] px-1 text-[10px] text-[#0C0C0C] font-display"
                            >
                                {cartCount}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid="mobile-menu-trigger"
                        className="md:hidden p-2.5 text-[#F4F4F0]"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label={t("nav.menu")}
                    >
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {searchOpen && (
                <div data-testid="search-bar" className="border-t border-white/5 bg-[#0C0C0C]/95 backdrop-blur-xl">
                    <div className="container-rail py-4">
                        <form onSubmit={submitSearch} className="flex items-stretch border border-white/15">
                            <Search className="self-center ml-4 h-4 w-4 text-[#A3A3A3]" strokeWidth={1.4} />
                            <input
                                type="text"
                                autoFocus
                                placeholder={t("search.placeholder")}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                data-testid="search-input"
                                className="flex-1 bg-transparent px-4 py-3 text-sm text-[#F4F4F0] placeholder:text-[#666] focus:outline-none"
                            />
                            <button
                                type="submit"
                                data-testid="search-submit"
                                className="bg-[#EDEDE6] px-5 text-[10px] tracking-[0.22em] uppercase text-[#0C0C0C] font-display"
                            >
                                {t("search.button")}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {mobileOpen && (
                <div data-testid="mobile-nav" className="md:hidden border-t border-white/5 bg-[#0C0C0C]/95 backdrop-blur-xl">
                    <div className="container-rail py-6 flex flex-col gap-5">
                        {NAV_KEYS.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                onClick={() => setMobileOpen(false)}
                                className="text-brutal text-sm text-[#F4F4F0]"
                            >
                                {t(`nav.${n.key}`)}
                            </NavLink>
                        ))}
                        <div className="pt-4 border-t border-white/5">
                            <LanguageSwitcher />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};
